import { useState, useEffect, useRef, useCallback } from 'react';
import { Button, logoDark, logoLight } from '@react-apps/design-system';
import { useSendVerification } from '../hooks/use-send-verification';

const COOLDOWN_MS = 5 * 60 * 1000;

/** Deadline do cooldown por e-mail em localStorage — sobrevive a F5 (useState não). */
function cooldownKey(email: string): string {
  return `verify-cooldown:${email}`;
}

interface VerificationWallProps {
  email: string;
  /** "Já confirmei, continuar" — re-tenta o sign-in com a sessão Firebase atual. */
  onContinue: () => Promise<void>;
  /** "Voltar" — encerra a sessão pendurada e volta ao formulário. */
  onBack: () => void;
  isBusy: boolean;
}

/**
 * Muro de verificação (§5.1): o login no Firebase deu certo, mas o back respondeu
 * 403 `ACCOUNT_NOT_ACTIVATED`. A conta **não** entra até o e-mail ser confirmado.
 * O store NÃO marca `isAuthenticated` aqui (só no sign-in bem-sucedido), então o
 * AuthGuard segue barrando o app.
 */
export function VerificationWall({
  email,
  onContinue,
  onBack,
  isBusy,
}: VerificationWallProps) {
  const { send, isLoading: isResending } = useSendVerification();
  const [remaining, setRemaining] = useState(0);
  const [stillUnverified, setStillUnverified] = useState(false);
  const mounted = useRef(true);

  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  // Tick do cooldown (1s), lido do localStorage.
  useEffect(() => {
    const tick = () => {
      const raw = localStorage.getItem(cooldownKey(email));
      const deadline = raw ? Number(raw) : 0;
      setRemaining(Math.max(0, deadline - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [email]);

  const handleResend = useCallback(() => {
    void send(email);
    localStorage.setItem(cooldownKey(email), String(Date.now() + COOLDOWN_MS));
    setRemaining(COOLDOWN_MS);
  }, [send, email]);

  const handleContinue = useCallback(async () => {
    setStillUnverified(false);
    await onContinue();
    // Se continuamos montados, o back não liberou → segue não-verificado.
    if (mounted.current) setStillUnverified(true);
  }, [onContinue]);

  const onCooldown = remaining > 0;
  const mm = Math.floor(remaining / 60000);
  const ss = Math.floor((remaining % 60000) / 1000)
    .toString()
    .padStart(2, '0');

  return (
    <div className="bg-(--color-bg-secondary) flex min-h-screen flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-1 flex-col items-center justify-center">
        <div className="mb-8">
          <img
            src={isDark ? logoDark : logoLight}
            alt="Primum"
            className="h-10 w-auto"
          />
        </div>

        <div className="mb-8 text-center">
          <h1 className="text-(--color-text-primary) text-2xl font-bold">
            Confirme seu e-mail para continuar
          </h1>
          <p className="text-(--color-text-secondary) mt-2 text-sm">
            Enviamos um link de confirmação para <strong>{email}</strong>. Abra
            o e-mail e clique no link para liberar seu acesso.
          </p>
        </div>

        <div className="border-(--color-border) bg-(--color-bg-primary) w-full space-y-4 rounded-2xl border p-8 shadow-sm">
          <p className="text-(--color-text-secondary) text-sm">
            Não encontrou? O e-mail pode ter caído na caixa de spam ou no lixo
            eletrônico.
          </p>

          {stillUnverified && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
              Ainda não confirmamos seu e-mail. Verifique se você abriu o link
              enviado para {email}.
            </div>
          )}

          <Button
            type="button"
            onClick={() => void handleContinue()}
            disabled={isBusy}
            className="w-full"
          >
            {isBusy ? 'Verificando…' : 'Já confirmei, continuar'}
          </Button>

          <Button
            type="button"
            variant="secondary"
            onClick={handleResend}
            disabled={onCooldown || isResending}
            className="w-full"
          >
            {onCooldown
              ? `Aguarde ${mm}:${ss} para reenviar`
              : isResending
                ? 'Reenviando…'
                : 'Reenviar e-mail'}
          </Button>

          <button
            type="button"
            onClick={onBack}
            className="text-(--color-text-secondary) hover:text-(--color-text-primary) w-full text-center text-sm transition-colors"
          >
            Voltar para o login
          </button>
        </div>
      </div>

      <footer className="py-6">
        <p className="text-(--color-text-secondary) text-xs">
          © 2026 Grupo Primum. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}
